import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { creditCostFor, fetchCreditsPricing } from '@/store/slices/aiCredits';

import { Icon, PageShell } from '../../components';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { HubGlyph } from '../website/components/HubGlyph';
import { ICON_PX, WebsiteHubCard } from '../website/components/WebsiteHubCard';
import { Grid } from '../website/components/WebsiteHubGrid/WebsiteHubGrid.styles';
import { Header, HubSubtitle, HubTitle } from '../website/website-hub.styles';
import { marketingAreaDescription, marketingAreaTitle } from './area-copy';
import { visibleMarketingAreas, type MarketingArea } from './areas';
import { MarketingGlyph } from './components/Glyphs';
import { useAdAreaCard } from './useAdAreaCard';

export type PageMarketingHubProps = {
  styleText?: string;
  className?: string;
};

/** Every tile glyph is drawn on the 24 viewBox (the shared Icon's and the hub's own): the ink's sweep spans it. */
const GLYPH_SPAN = 24;

/** One area's card: its library read (the same hook for every area, so always called), its copy and its glyph,
 *  painted on the neutral tile in its area's sweep, the hue of the card's glow. */
const MarketingHubCardSlot = ({ area, index }: { area: MarketingArea; index: number }): React.ReactElement => {
  const { t } = useTranslation();
  const view = useAdAreaCard(area.id, area.path);
  const { glyph } = area;
  const icon = (
    <HubGlyph area={area.id} span={GLYPH_SPAN}>
      {(ink) =>
        glyph.kind === 'icon' ? (
          <Icon name={glyph.name} size={ICON_PX} color={ink} />
        ) : (
          <MarketingGlyph name={glyph.name} size={ICON_PX} color={ink} />
        )
      }
    </HubGlyph>
  );
  return (
    <WebsiteHubCard
      area={area.id}
      title={marketingAreaTitle(t, area.id)}
      description={marketingAreaDescription(t, area.id)}
      icon={icon}
      to={area.path}
      view={view}
      enterIndex={index}
      testId={`hub-card-${area.id}`}
    />
  );
};

/**
 * The Marketing hub (`/account/marketing-assets`), as the Website hub: one card per Marketing area, in the areas'
 * order and each under the flag that shows its library, saying how many ads it holds (with the newest one's poster)
 * and whether one is generating or still a draft. The whole card opens the area, headed there by a back button to
 * this hub. Each card loads its own data; the cards offer no actions, so the page owns nothing they share.
 */
const PageMarketingHub = ({ styleText = '', className }: PageMarketingHubProps): React.ReactElement => {
  const { isEnabled } = useFeatureGate();
  const dispatch = useAppDispatch();
  // Video Ads' library needs its per-beat price, as the page's own switch for it does: read the price table here too,
  // so a visit that starts on the hub does not hide the card.
  const videoPriced = useAppSelector((state) => creditCostFor(state.aiCredits?.pricing, 'video_beat')) !== undefined;
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);
  return (
    <PageShell styleText={styleText} className={className}>
      <Header>
        <HubTitle text="Marketing" tag="h1" />
        <HubSubtitle text="Everything you advertise your casino with." tag="paragraph" />
      </Header>
      <Grid>
        {visibleMarketingAreas(isEnabled, videoPriced).map((area, index) => (
          <MarketingHubCardSlot key={area.id} area={area} index={index} />
        ))}
      </Grid>
    </PageShell>
  );
};

export default PageMarketingHub;
